import { fetcher, fetcherSWR } from '@utils/data-fetching/fetcher';
import {
  AcceptConsentPayload,
  ConsentAcceptChannel,
  ConsentPageView,
  CreateEndorsementRequestPayload,
  EndorsementRequestView,
  OwnEndorsementView,
  RecruiterEndorsementView,
  SetEndorsementHiddenPayload,
} from '@type/endorsements';

/**
 * SWR key builder for the recruiter view. Returns the exact tuple `fetcherSWR`
 * expects, so `useSWR(endorsementsForUserKey(slug, locale), fetcherSWR)`
 * pulls the map keyed by experience id.
 */
export const endorsementsForUserKey = (
  slug: string | undefined,
  locale: string,
): [string, string, {}] | null => (
  slug ? [`/v1/resume/${encodeURIComponent(slug)}/endorsements`, locale, {}] : null
);

export type EndorsementsByExperienceId = Record<string, RecruiterEndorsementView[]>;

export const fetchEndorsementsByExperienceIdSWR = (
  args: [string, string, {}],
) => fetcherSWR<EndorsementsByExperienceId>(args);

/**
 * SWR key for the candidate's own endorsements (CV-edit page). Unlike the
 * recruiter view it includes hidden rows, each flagged, so they can be
 * unhidden. Shared by the section and the page's section nav — SWR dedupes.
 */
export const ownEndorsementsKey = (locale: string): [string, string, {}] => (
  ['/v1/resume/endorsements', locale, {}]
);

export const fetchOwnEndorsementsSWR = (
  args: [string, string, {}],
) => fetcherSWR<OwnEndorsementView[]>(args);

/**
 * SWR key builder for the candidate-side "References" section on the work-
 * experience edit form. Returns null when experienceId is falsy so the hook
 * pauses (e.g. new-experience form before it's been saved).
 */
export const endorsementRequestsForExperienceKey = (
  experienceId: number | undefined,
  locale: string,
): [string, string, {}] | null => (
  experienceId
    ? [`/v1/resume/work-experience/${experienceId}/endorsement-requests`, locale, {}]
    : null
);

export const fetchEndorsementRequestsForExperienceSWR = (
  args: [string, string, {}],
) => fetcherSWR<EndorsementRequestView[]>(args);

/**
 * Candidate-side create. Server enforces:
 *   - ROLE_PREVIOUS_ADMIN gate (only impersonating admins during prod-test rollout),
 *   - cap of 3 nominees per experience (409 CONFLICT),
 *   - duplicate phone hash (409 CONFLICT),
 *   - invalid phone (422).
 */
export const createEndorsementRequest = async (
  experienceId: number,
  payload: CreateEndorsementRequestPayload,
  locale: string,
): Promise<EndorsementRequestView | void> => fetcher<EndorsementRequestView>(
  `/v1/resume/work-experience/${experienceId}/endorsement-requests`,
  locale,
  {
    method: 'POST',
    payload,
    // Backend controller uses `#[MapRequestPayload]` which is strict about
    // Content-Type — without these two the request comes back as 415.
    stringifyPayload: true,
    headers: { 'Content-Type': 'application/json' },
  },
);

/** Toggle recruiter visibility of an individual endorsement. 204 on success; throws otherwise. */
export const setEndorsementHidden = async (
  endorsementId: number,
  hidden: boolean,
  locale: string,
): Promise<void> => {
  const payload: SetEndorsementHiddenPayload = { hidden };
  const result = await fetcher<{ ok: boolean }>(
    `/v1/resume/endorsements/${endorsementId}`,
    locale,
    {
      method: 'PUT',
      payload,
      // Same MapRequestPayload strictness applies here.
      stringifyPayload: true,
      headers: { 'Content-Type': 'application/json' },
      expectEmptyResponse: true,
      // The fetcher resolves on 4xx/5xx too; the status is the only way to
      // tell a failed toggle from a successful one.
      withResponseHeaders: true,
    },
  );
  if (!result?.ok) {
    throw new Error(`setEndorsementHidden failed for endorsement ${endorsementId}`);
  }
};

// ---------------------------------------------------------------------------
// Public consent-page API. Called from the /r/consent/[token] React page.
// Referee is unauthenticated (typically not a BJ user) — no session cookie is
// sent; the URL token is the whole authorization.
// ---------------------------------------------------------------------------

/**
 * Fetch consent-page context by token. Returns null-ish (`undefined` via fetcher void)
 * on 404 — the caller should render a "link no longer active" state.
 */
export const fetchConsentContext = async (
  token: string,
  locale: string,
): Promise<ConsentPageView | void> => fetcher<ConsentPageView>(
  `/v1/endorsements/consent/${encodeURIComponent(token)}`,
  locale,
  { method: 'GET', credentials: 'omit' },
);

export const acceptConsent = async (
  token: string,
  channel: ConsentAcceptChannel,
  locale: string,
): Promise<void> => {
  const payload: AcceptConsentPayload = { channel };
  await fetcher<void>(
    `/v1/endorsements/consent/${encodeURIComponent(token)}/accept`,
    locale,
    {
      method: 'POST',
      payload,
      stringifyPayload: true,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      expectEmptyResponse: true,
    },
  );
};

export const declineConsent = async (
  token: string,
  locale: string,
): Promise<void> => {
  await fetcher<void>(
    `/v1/endorsements/consent/${encodeURIComponent(token)}/decline`,
    locale,
    {
      method: 'POST',
      credentials: 'omit',
      expectEmptyResponse: true,
    },
  );
};
